import React from 'react';
import { useLanguage } from '../context/LanguageProvider';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { motion } from 'framer-motion';
import { MessageSquare, Calendar, Camera, Edit3, Sparkles, Send } from 'lucide-react';
import { FallbackUI } from './FallbackUI';

const stepIcons = [
  <MessageSquare size={24} key="consult" />,
  <Calendar size={24} key="plan" />,
  <Camera size={24} key="capture" />,
  <Edit3 size={24} key="edit" />,
  <Sparkles size={24} key="ai" />,
  <Send size={24} key="deliver" />,
];

export const WorkflowSection: React.FC = () => {
  const { t } = useLanguage();
  const { ref, isVisible } = useScrollReveal();

  const steps = t.workflow.steps;

  if (!steps || steps.length === 0) {
    return <FallbackUI message="Workflow loading..." />;
  }

  return (
    <section id="workflow" className="relative py-24 md:py-32 bg-vilarias-dark">
      <div className="absolute inset-0 bg-hero-glow opacity-20" />

      <div className="section-padding relative z-10" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-serif text-3xl md:text-5xl text-white mb-4">
            {t.workflow.headline}
          </h2>
          <div className="h-px w-16 bg-vilarias-accent mx-auto" />
        </motion.div>

        <div className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-vilarias-accent via-vilarias-accent/50 to-transparent md:-translate-x-px" />

            {steps.map((step, index) => {
              const isLeft = index % 2 === 0;
              return (
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: isLeft ? -30 : 30 }}
                  animate={isVisible ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className={`relative flex items-center gap-8 mb-12 last:mb-0 ${
                    isLeft ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  {/* Content */}
                  <div className={`flex-1 ${isLeft ? 'md:text-right' : 'md:text-left'}`}>
                    <div className={`glass-card rounded-xl p-6 inline-block max-w-sm ${
                      isLeft ? 'md:ml-auto' : 'md:mr-auto'
                    }`}>
                      <span className="text-vilarias-accent text-xs tracking-wider uppercase mb-2 block">
                        Step {index + 1}
                      </span>
                      <h3 className="text-lg font-medium text-white">{step}</h3>
                    </div>
                  </div>

                  {/* Icon node */}
                  <div className="relative z-10 w-12 h-12 rounded-full bg-vilarias-accent border-4 border-vilarias-dark flex items-center justify-center text-white flex-shrink-0 shadow-[0_0_20px_rgba(0,141,255,0.4)]">
                    {stepIcons[index] || <Sparkles size={24} />}
                  </div>

                  {/* Spacer */}
                  <div className="flex-1 hidden md:block" />
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
